import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ListPlus, SearchX, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/ui/EmptyState";
import { PeopleSearchSort } from "@/types/peopleSearch";
import { ResultsTable } from "@/components/queries/ResultsTable";
import { AddToListDialog } from "@/components/queries/AddToListDialog";
import { usePeopleSearch } from "@/contexts/PeopleSearchContext";
import { usePeopleSearchResults } from "@/hooks/peopleSearch/usePeopleSearchResults";
import { PEOPLE_SEARCH_PAGE_SIZE } from "@/utils/peopleSearchExecutor";

export function ResultsPane() {
	const { t } = useTranslation();
	const { applied, hasApplied, page, setPage, sort, setSort } = usePeopleSearch();
	const { rows, total, isLoading, isError, refetch } = usePeopleSearchResults(applied, page, hasApplied, sort);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [addToListOpen, setAddToListOpen] = useState(false);

	const totalPages = Math.max(1, Math.ceil(total / PEOPLE_SEARCH_PAGE_SIZE));

	return (
		<div className="flex flex-col h-full min-h-0 gap-3">
			<div className="flex items-center justify-between shrink-0">
				<h3 className="text-base font-semibold">
					{t("peopleSearch.results.title", "Results")}
					{hasApplied && !isLoading && (
						<span className="ml-2 text-sm font-normal text-muted-foreground" aria-live="polite">
							{t("peopleSearch.results.total", "{{count}} profiles", { count: total })}
						</span>
					)}
				</h3>
				<div className="flex items-center gap-2">
					{selectedIds.length > 0 && (
						<Button variant="outline" size="sm" onClick={() => setAddToListOpen(true)}>
							<ListPlus className="h-4 w-4 mr-1" />
							{t("peopleSearch.results.addToList", "Add {{count}} to list", { count: selectedIds.length })}
						</Button>
					)}
					{hasApplied && (
						<Select value={sort} onValueChange={value => setSort(value as PeopleSearchSort)}>
							<SelectTrigger
								className="h-8 w-[150px] text-sm"
								aria-label={t("peopleSearch.results.sortLabel", "Sort by")}
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="newest">{t("peopleSearch.results.sort.newest", "Newest first")}</SelectItem>
								<SelectItem value="oldest">{t("peopleSearch.results.sort.oldest", "Oldest first")}</SelectItem>
								<SelectItem value="nameAsc">{t("peopleSearch.results.sort.nameAsc", "Name A–Z")}</SelectItem>
								<SelectItem value="nameDesc">{t("peopleSearch.results.sort.nameDesc", "Name Z–A")}</SelectItem>
							</SelectContent>
						</Select>
					)}
				</div>
			</div>

			<div className="flex-1 min-h-0 overflow-y-auto">
				{isError ? (
					<Alert variant="destructive">
						<AlertCircle className="h-4 w-4" />
						<AlertTitle>
							{t("peopleSearch.results.errorTitle", "Something went wrong loading results")}
						</AlertTitle>
						<AlertDescription className="flex flex-col items-start gap-3">
							{t("peopleSearch.results.errorDescription", "Your search couldn't be run. Check your connection and try again.")}
							<Button variant="outline" size="sm" onClick={refetch}>
								{t("peopleSearch.results.retry", "Try again")}
							</Button>
						</AlertDescription>
					</Alert>
				) : (
				<ResultsTable
					results={rows}
					isLoading={isLoading}
					hasRun={hasApplied}
					dense
					selectedIds={selectedIds}
					onSelectionChange={setSelectedIds}
					notRunContent={
						<EmptyState
							icon={Users}
							title={t("peopleSearch.results.notRunTitle", "Build your search")}
							description={t("peopleSearch.results.notRunDescription", "Pick filters on the left, then press Show profiles")}
						/>
					}
					emptyContent={
						<EmptyState
							icon={SearchX}
							title={t("peopleSearch.results.emptyTitle", "No profiles match")}
							description={t("peopleSearch.results.emptyDescription", "Try removing a filter or broadening your keywords")}
						/>
					}
				/>
				)}
			</div>

			{hasApplied && !isError && totalPages > 1 && (
				<div className="flex items-center justify-between shrink-0">
					<Button
						variant="outline"
						size="sm"
						disabled={page <= 1}
						onClick={() => setPage(page - 1)}
					>
						{t("peopleSearch.results.previous", "Previous")}
					</Button>
					<span className="text-sm text-muted-foreground">
						{t("peopleSearch.results.pageOf", "Page {{page}} of {{total}}", { page, total: totalPages })}
					</span>
					<Button
						variant="outline"
						size="sm"
						disabled={page >= totalPages}
						onClick={() => setPage(page + 1)}
					>
						{t("peopleSearch.results.next", "Next")}
					</Button>
				</div>
			)}

			<AddToListDialog
				open={addToListOpen}
				onOpenChange={setAddToListOpen}
				candidateIds={selectedIds}
				onSuccess={() => setSelectedIds([])}
			/>
		</div>
	);
}
